import { useState, useEffect, useContext } from 'react';

import { Button, Input, Label, Textarea } from '@@/ui/primitives';
import { FileCode2Icon, TrashIcon } from 'lucide-react';
import { EntityRecord } from '../../api/api-entities';
import { formatDate } from 'date-fns';
import { useDeleteEntity } from '../../hooks/use-entities';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { Spinner } from '@@/ui/components';
import { AuthContext } from '@@/contexts/AuthContext';

export function EntityInfoForm({
  entityInfo,
  entity,
  onChange,
  disabled = false
}: {
  entityInfo: {
    name: string;
    description?: string;
  };
  entity: EntityRecord;
  onChange: (
    updates: Partial<{
      name: string;
      description?: string;
    }>
  ) => void;
  disabled?: boolean;
}) {
  const { getPathWithInstanceAlias } = useContext(AuthContext);

  const [name, setName] = useState(entityInfo.name);
  const [description, setDescription] = useState(entityInfo.description || '');
  const deleteEntityMutation = useDeleteEntity();
  const navigate = useNavigate();

  useEffect(() => {
    setName(entityInfo.name);
    setDescription(entityInfo.description || '');
  }, [entityInfo]);

  const handleDelete = async () => {
    if (!entity) return;

    const confirmDelete = window.confirm('Are you sure you want to delete this entity?');
    if (!confirmDelete) return;

    try {
      await deleteEntityMutation.mutateAsync(entity.entityId);
      navigate(getPathWithInstanceAlias(`/portal/dfc/browser/${entity.parentId}`));
      toast.success('Entity has been deleted successfully.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete entity');
    }
  };
  const handleNameChange = (value: string) => {
    setName(value);
    onChange({
      name: value,
      description: description
    });
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);

    onChange({
      name: name,
      description: value
    });
  };

  return (
    <div className='flex flex-col h-full gap-4'>
      <div className='flex items-center gap-2'>
        <FileCode2Icon className='h-5 w-5 text-neutral-950' />
        <h2 className='font-semibold text-neutral-950'>Details</h2>
      </div>
      <div className='flex-1 flex flex-col gap-4'>
        <span className='font-mono text-sm bg-neutral-100 py-2 px-2.5 rounded'>{entity.entityId}</span>
        <span className='font-mono text-sm bg-neutral-100 py-2 px-2.5 rounded'>{entity.path ?? 'No path defined'}</span>
        <div className='space-y-2'>
          <Label htmlFor='entity-name' className='text-sm font-medium'>
            Name
          </Label>
          <Input
            id='entity-name'
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder='Entity name'
            className='text-sm!'
            disabled={disabled}
          />
        </div>

        <div className='space-y-2'>
          <Label htmlFor='entity-description' className='text-sm font-medium'>
            Description
          </Label>
          <Textarea
            id='entity-description'
            value={description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            placeholder='Describe what this entity represents...'
            disabled={disabled}
            rows={3}
            className='text-sm! resize-none'
          />
        </div>

        <div className='flex flex-col items-end px-3 py-2'>
          <span className='text-xs text-neutral-950 font-medium'>{formatDate(entity.createdAt, 'PPpp')}</span>
          <span className='text-xs text-neutral-600'>By {entity.createdBy}</span>
        </div>
      </div>
      <div className='flex flex-col gap-2'>
        <Button
          variant='destructive'
          size='lg'
          onClick={handleDelete}
          disabled={deleteEntityMutation.isPending}
          className='text-sm! font-medium! w-full'
        >
          {deleteEntityMutation.isPending ? <Spinner /> : <TrashIcon className='size-3.5' />}
          Delete Entity
        </Button>
        <p className='text-xs text-neutral-600'>
          Deleting an Entity is permanent. Sub-Entities and items under it will also be deleted.
        </p>
      </div>
    </div>
  );
}
